/* eslint max-lines: [2, {"max": 150, "skipComments": true, "skipBlankLines": true}] */
import './graphiql.scss';

import GraphiQL from 'graphiql';
import { forEach } from 'lodash';
import React, { useCallback, useMemo, useRef } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';

import { getAccessToken } from '../../lib/auth/cognito';
import { fetchIntrospectionQuery } from '../../lib/auth/restApi';
import { tryFunction } from '../../lib/utils/qol';
import { saveGqlQueries } from '../../store/asyncActions/collection';
import GraphQlFooter from './components/footer';
import CustomToolbar from './components/toolbar';
import WorkspaceSidebar from './components/workspace';
import CreateCollectionDialog from './dialogs/createCollection';
import EditCollectionDialog from './dialogs/editCollection';
import EditQueryDialog from './dialogs/editQuery';
import ManageRequestHeadersDialog from './dialogs/manageHeaders';
import OverwriteQueryDialog from './dialogs/overWriteQuery';
import SaveQueryDialog from './dialogs/saveQuery';
import ManageRsaSignatureDialog from './dialogs/manageRsaSignature';
import { generateRsaSignature } from '../../lib/utils/rsaSignature';

const defaultQuery = `# Welcome to Lumenite
#
# Lumenite is an in-browser tool for writing, validating, and
# testing GraphQL queries.
#
# Type queries into this side of the screen, and you will see intelligent
# typeaheads aware of the current GraphQL type schema and live syntax and
# validation errors highlighted within the text.
#
# GraphQL queries typically start with a "{" character. Lines that starts
# with a # are ignored.
#
# An example GraphQL query might look like:
#
#     {
#       field(arg: "value") {
#         subField
#       }
#     }
#
# Keyboard shortcuts:
#
#  Prettify Query:  Shift-Ctrl-P
#
#     Merge Query:  Shift-Ctrl-M
#
#       Run Query:  Ctrl-Enter
#
#   Auto Complete:  Ctrl-Space
#
`;

const GraphQLEditor = () => {
  const graphiql = useRef(null);
  const dispatch = useDispatch();
  const { authType, apiKey, authorizationToken, userpoolId, userpoolClientId, graphqlEndpoint, idToken } = useSelector(
    ({ auth }) => auth,
    shallowEqual
  );

  const requestHeadersList = useSelector(({ workspace }) => workspace.requestHeaders);
  const requestHeaders = useMemo(() => {
    const headers = {};
    forEach(requestHeadersList, requestHeader => {
      if (requestHeader.enabled) {
        headers[requestHeader.name] = requestHeader.value;
      }
    });
    return headers;
  }, [requestHeadersList]);

  const rsaSignaturePieces = useSelector(({ workspace }) => workspace.rsaSignaturePieces);

  const graphQLFetcher = useCallback(
    async graphQLParams => {
      const token =
        (authType === 'lambda' && authorizationToken) ||
        (authType === 'cognito' && (await getAccessToken({ userpoolId, userpoolClientId }))) ||
        (['okta', 'oidc'].includes(authType) && idToken);

      const params = (token && { token }) || (apiKey && { apiKey });

      const rsaSignature = generateRsaSignature(authType, rsaSignaturePieces, requestHeaders, graphQLParams);
      return fetchIntrospectionQuery({ graphqlEndpoint, graphQLParams, requestHeaders, rsaSignature, ...params });
    }, 
    [apiKey, authType, graphqlEndpoint, userpoolClientId, userpoolId, requestHeaders, rsaSignaturePieces, idToken, authorizationToken]);

  const prettify = () => tryFunction(graphiql.current.handlePrettifyQuery);
  const mergeQuery = () => tryFunction(graphiql.current.handleMergeQuery);
  const copyQuery = () => tryFunction(graphiql.current.handleCopyQuery);
  const toggleHistory = () => tryFunction(graphiql.current.handleToggleHistory);

  const saveQuery = formData => {
    try {
      const query = graphiql.current.getQueryEditor().getValue();
      const variable = graphiql.current.getVariableEditor().getValue();
      dispatch(saveGqlQueries({ query, variable, ...formData }));
    } catch (error) {
      toast.error(`Error: ${error}`);
    }
  };

  const setQuery = useCallback(({ query, variable }) => {
    graphiql.current.getQueryEditor().setValue(query);
    graphiql.current.getVariableEditor().setValue(variable);
  }, []);

  const getQueryData = useCallback(() => {
    try {
      const query = graphiql.current.getQueryEditor().getValue();
      const variable = graphiql.current.getVariableEditor().getValue();
      return { query, variable };
    } catch (error) {
      toast.error(`Error: ${error}`);
    }
    return {};
  }, []);

  return (
    <div className='editor_div'>
      <WorkspaceSidebar setQuery={setQuery} />
      <CreateCollectionDialog />
      <EditCollectionDialog />
      <SaveQueryDialog save={saveQuery} />
      <EditQueryDialog />
      <ManageRequestHeadersDialog />
      {authType === 'apiKey' && <ManageRsaSignatureDialog />}
      <OverwriteQueryDialog getQueryData={getQueryData} />
      <GraphiQL
        ref={graphiql}
        fetcher={graphQLFetcher}
        defaultQuery={defaultQuery}
        editorTheme='orion'
      >
        <GraphiQL.Logo>
          <span className='editor_div--lumenite_title'>Lumenite</span>
        </GraphiQL.Logo>
        <GraphiQL.Toolbar>
          <CustomToolbar
            prettify={prettify}
            merge={mergeQuery}
            copy={copyQuery}
            toggleHistory={toggleHistory}
          />
        </GraphiQL.Toolbar>
        <GraphiQL.Footer>
          <GraphQlFooter />
        </GraphiQL.Footer>
      </GraphiQL>
    </div>
  );
};

export default GraphQLEditor;
